import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { AuthState, AUTH_CONSTANTS } from 'app/core/auth/auth.types';
import { Observable, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';

/**
 * Closure state for loop prevention
 */
const recentRedirects = new Map<string, number>();
const REDIRECT_COOLDOWN = 5000; // 5 seconds

function hasRecentRedirect(key: string): boolean {
    const last = recentRedirects.get(key);
    if (!last) return false;
    return (Date.now() - last) < REDIRECT_COOLDOWN;
}

function recordRedirect(key: string): void {
    recentRedirects.set(key, Date.now());
    // Clean up after cooldown
    setTimeout(() => {
        const now = Date.now();
        recentRedirects.forEach((ts, k) => {
            if ((now - ts) > REDIRECT_COOLDOWN) recentRedirects.delete(k);
        });
    }, REDIRECT_COOLDOWN);
}

/**
 * Enhanced NoAuthGuard (Angular 19 functional CanActivateFn)
 *
 * Protects auth routes (sign-in, sign-up, etc.) and redirects
 * authenticated users. Prevents infinite loops.
 */
export const NoAuthGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const routePath = state.url.replace(/^\//, ''); // Remove leading slash

    return authService.checkAuthentication().pipe(
        timeout(AUTH_CONSTANTS.AUTH_CHECK_TIMEOUT),
        map(authStatus => {
            switch (authStatus.state) {
                case AuthState.AUTHENTICATED:
                    return handleAuthenticatedUser(routePath, router);

                case AuthState.EXPIRED:
                    return handleExpiredSession(routePath, router);

                case AuthState.UNAUTHENTICATED:
                    return true;

                case AuthState.ERROR:
                    return handleAuthError(routePath, router);

                case AuthState.INITIALIZING:
                default:
                    return true;
            }
        }),
        catchError(() => {
            // On error, allow access to auth pages (fail-safe)
            return of(true as boolean | UrlTree);
        })
    );
};

function handleAuthenticatedUser(routePath: string, router: Router): boolean | UrlTree {
    // Allow re-authentication flows
    const reAuthPaths = ['sign-out', 'sign-in'];
    if (reAuthPaths.some(p => routePath.includes(p))) {
        return true;
    }

    // Prevent infinite loops
    if (hasRecentRedirect('authenticated-user')) {
        return true;
    }

    recordRedirect('authenticated-user');
    return router.createUrlTree(['/sessions']);
}

function handleExpiredSession(routePath: string, router: Router): boolean | UrlTree {
    // For expired sessions, allow access to sign-in/sign-out
    if (routePath.includes('sign-in') || routePath.includes('sign-out')) {
        return true;
    }

    return router.createUrlTree(['/sign-in'], {
        queryParams: {
            message: 'Your session has expired. Please sign in again.',
            reason: 'expired'
        }
    });
}

function handleAuthError(routePath: string, router: Router): boolean | UrlTree {
    // On auth errors, allow access to sign-in/sign-out
    if (routePath.includes('sign-in') || routePath.includes('sign-out')) {
        return true;
    }

    return router.createUrlTree(['/sign-in'], {
        queryParams: {
            message: 'Authentication system error. Please try signing in.',
            reason: 'error'
        }
    });
}
